import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  FileText, Plus, Pencil, Trash2, Eye, EyeOff, 
  ArrowLeft, LogOut, Save, X, Calendar
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import FileUpload from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [featuredImage, setFeaturedImage] = useState([]);
  const { authenticated, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    author: '',
    status: 'draft',
    created_at: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      fetchPosts();
    }
  }, [authenticated]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/blog/posts`, {
        headers: getAuthHeader()
      });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      featured_image: '',
      author: '',
      status: 'draft',
      created_at: new Date().toISOString().split('T')[0]
    });
    setFeaturedImage([]);
    setShowAddForm(false);
    setEditingPost(null);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      featured_image: post.featured_image || '',
      author: post.author || '',
      status: post.status || 'draft',
      created_at: post.created_at ? post.created_at.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    // Set featured image if exists
    if (post.featured_image) {
      setFeaturedImage([{
        id: 'existing',
        url: post.featured_image,
        is_primary: true
      }]);
    } else {
      setFeaturedImage([]);
    }
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    try {
      const submitData = {
        ...formData,
        featured_image: featuredImage.length > 0 ? featuredImage[0].url : '',
        created_at: new Date(formData.created_at).toISOString()
      };

      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost._id}`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/admin/blog/posts`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Post created successfully');
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleDelete = async (postId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, {
        headers: getAuthHeader()
      });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const toggleStatus = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/admin/blog/posts/${post._id}`, {
        status: newStatus
      }, {
        headers: getAuthHeader()
      });
      toast.success(`Post ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      fetchPosts();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Blog Management</h1>
            <p className="text-sm text-gray-500">Create, edit, and manage blog posts</p>
          </div>
          <Button onClick={() => { resetForm(); setShowAddForm(true); }} data-testid="add-post-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Post
          </Button>
        </div>

        {/* Add/Edit Post Form */}
        {showAddForm && (
          <Card className="mb-8" data-testid="post-form">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {editingPost ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                {editingPost ? 'Edit Post' : 'Add New Post'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <Input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter post title"
                      required
                      data-testid="post-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                    <Input
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      placeholder="Author name"
                      data-testid="post-author-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Publication Date
                    </label>
                    <Input
                      type="date"
                      name="created_at"
                      value={formData.created_at}
                      onChange={handleInputChange}
                      data-testid="post-date-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Set or change the date this post was published</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md"
                      data-testid="post-status-select"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Featured Image
                    </label>
                    <FileUpload
                      category="blog"
                      files={featuredImage}
                      onChange={setFeaturedImage}
                      multiple={false}
                      showPrimary={false}
                      sortable={false}
                      label="Upload Featured Image"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                    <textarea
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Brief description of the post"
                      data-testid="post-excerpt-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      rows={8}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Write your blog post content here..."
                      data-testid="post-content-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" data-testid="save-post-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {editingPost ? 'Update Post' : 'Create Post'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Posts List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              All Posts ({posts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <p className="mt-2 text-gray-600">Loading posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                No posts found. Click "Add Post" to create your first blog post.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="posts-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-4 font-semibold">Post</th>
                      <th className="text-left p-4 font-semibold">Author</th>
                      <th className="text-center p-4 font-semibold">
                        <Calendar className="inline h-4 w-4 mr-1" />
                        Date
                      </th>
                      <th className="text-center p-4 font-semibold">Status</th>
                      <th className="text-center p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <tr key={post._id} className={`border-b hover:bg-gray-50 ${post.status !== 'published' ? 'opacity-60' : ''}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {post.featured_image && (
                              <img
                                src={post.featured_image}
                                alt={post.title}
                                className="w-16 h-12 rounded object-cover"
                              />
                            )}
                            <div>
                              <p className="font-semibold text-gray-900">{post.title}</p>
                              <p className="text-sm text-gray-500 line-clamp-1">{post.excerpt}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-gray-600">
                          {post.author || 'Admin'}
                        </td>
                        <td className="p-4 text-center">
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                            {formatDate(post.created_at)}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            post.status === 'published' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {post.status === 'published' ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => toggleStatus(post)}
                              className={`p-2 rounded-full transition-colors ${
                                post.status === 'published'
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                              data-testid={`toggle-status-${post._id}`}
                            >
                              {post.status === 'published' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => handleEdit(post)}
                              className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                              title="Edit Post"
                              data-testid={`edit-post-${post._id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(post._id, post.title)}
                              className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                              title="Delete Post"
                              data-testid={`delete-post-${post._id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Tips for Blog Posts</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Publication Date:</strong> You can backdate or forward-date posts using the date picker</li>
            <li>• <strong>Status:</strong> Draft posts are hidden from the public blog page</li>
            <li>• <strong>Featured Image:</strong> Use high-quality images (recommended: 1200x630 pixels)</li>
            <li>• <strong>Excerpt:</strong> A brief summary shown in blog listings and search results</li>
          </ul>
        </div>
    </div>
  );
};

export default AdminBlog;
